import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    NewSystemColorDialogComponent,
    SystemColorDialogMode,
} from '@components/new-system-color-dialog/new-system-color-dialog.component';
import { defaultNumberInputArrowedKey, KeyFilterSerivce } from '@service/key-filter.service';
import { SystemColorService } from '@service/system-color.service';
import { SystemColor, SystemColorType } from 'icreate/common/models/system-color';
import { Vessel } from 'icreate/common/models/vessel';

@Component({
    selector: 'app-system-color-input',
    templateUrl: './system-color-input.component.html',
    styleUrls: ['./system-color-input.component.css'],
    standalone: false,
})
export class SystemColorInputComponent implements OnInit {
    readonly CUSTOM_TYPE = SystemColorType.Custom;
    readonly ALLOWED_KEYS = defaultNumberInputArrowedKey;

    @Input() data!: SystemColor;
    @Input() custom: boolean = false;
    @Input() offOptions: boolean = false;
    @Input() createMode: boolean = false;
    @Input() readonly: boolean = false;
    @Input() selected: boolean = false;
    @Input() vessel: Vessel = null;
    @Input() canEditVessel: boolean = false;

    @Output() onColorChange = new EventEmitter<string>();

    value: string;
    rgb: string;

    prevRgb: string;

    rgbErrorMessage: string;
    isColorPickerOpen: boolean;
    initialValue: string;

    constructor(
        private systemColorService: SystemColorService,
        private dialog: MatDialog,
        private keyFilterService: KeyFilterSerivce
    ) {}

    ngOnInit(): void {
        this.value = this.data.color;
        this.initialValue = this.value;
        this.parseRGBA(this.value);
    }

    async colorPickerClick(shouldOpen) {
        this.isColorPickerOpen = shouldOpen;
        if (!shouldOpen) {
        }
    }

    @HostListener('document:click', ['$event'])
    async handleOutsideClick(event: MouseEvent) {
        if (this.isColorPickerOpen || this.createMode) return;

        const target = event.target as HTMLElement;
        if (!target.closest('.color-input-wrapper')) {
            this.isColorPickerOpen = false;
            await this.rbgaSave();
        }
    }

    parseRGBA(rgbaString: string) {
        const match = rgbaString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d\.]+)?\)/);

        if (!match) {
            return;
        }

        const r = parseInt(match[1], 10);
        const g = parseInt(match[2], 10);
        const b = parseInt(match[3], 10);

        this.prevRgb = this.rgb;

        this.rgb = `${r}, ${g}, ${b}`;
    }

    convertRgbToRgba(rgbString) {
        const rgbRegex = /^rgb\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)$/;
        const match = rgbString.match(rgbRegex);

        if (match) {
            const r = match[1];
            const g = match[2];
            const b = match[3];
            return `rgba(${r}, ${g}, ${b}, 1)`;
        }

        return rgbString;
    }

    isRgbColor(colorString) {
        const rgbRegex = /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i;
        const rgbaRegex = /^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d*\.?\d+)\s*\)$/i;

        if (rgbRegex.test(colorString)) {
            return true;
        } else if (rgbaRegex.test(colorString)) {
            return false;
        }
    }

    colorChange() {
        if (this.isRgbColor(this.value)) {
            this.value = this.convertRgbToRgba(this.value);
        }
        this.parseRGBA(this.value);
        this.onColorChange.emit(this.value);
    }

    rgbChange() {
        const rgbPattern = /^\s*(0|[1-9]\d*)\s*,\s*(0|[1-9]\d*)\s*,\s*(0|[1-9]\d*)\s*$/;
        const match = this.rgb.match(rgbPattern);

        if (match && match.length === 4) {
            const r = parseInt(match[1], 10);
            const g = parseInt(match[2], 10);
            const b = parseInt(match[3], 10);

            if (r > 255 || g > 255 || b > 255) {
                this.rgbErrorMessage = 'system-color.input.alpha.error-message';
                return;
            }

            this.value = `rgba(${r}, ${g}, ${b}, 1)`;

            this.onColorChange.emit(this.value);
            this.rgbErrorMessage = '';
        } else {
            this.rgbErrorMessage = 'system-color.input.rgba.error-message';
        }
    }

    async rbgaSave() {
        if (this.createMode) return;
        if (this.initialValue == this.value) return;
        try {
            if (!this.rgbErrorMessage) {
                await this.systemColorService.update({
                    ...this.data,
                    color: this.value,
                });
                this.initialValue = this.value;
            } else {
                this.value = this.data.color;
                this.parseRGBA(this.value);

                this.rgbErrorMessage = '';
            }
        } catch (error) {
            console.log(error);
        }
    }

    async removeNonCustomColors() {
        if (!this.canEditVessel) return;
        try {
            await this.systemColorService.update({
                ...this.data,
                color: '',
            });
        } catch (error) {
            console.log(error);
        }
    }

    async editCustomColor() {
        if (!this.canEditVessel) return;
        try {
            const systemColorList = await this.systemColorService.getAll(this.vessel.id);
            this.dialog.open(NewSystemColorDialogComponent, {
                width: '474px',
                autoFocus: false,
                data: {
                    id: this.data.id,
                    name: this.data.name,
                    color: this.data.color,
                    type: SystemColorType.Custom,
                    isEditable: true,
                    mode: SystemColorDialogMode.edit,
                    vesselId: this.data.vesselId,
                    systemColorList,
                },
                disableClose: true,
            });
        } catch (error) {
            console.log(error);
        }
    }

    deleteCustomColor() {
        if (!this.canEditVessel) return;
        this.systemColorService.delete(this.data?.id);
    }

    validRGBKeyDown(event: KeyboardEvent) {
        const shouldBlockKey = this.keyFilterService.shouldPreventDefault(event, [...this.ALLOWED_KEYS, ',']);
        if (shouldBlockKey) event.preventDefault();
    }
}
